/**
 * Pattern to match markdown links with sandbox: protocol.
 * Captures the link text and raw sandbox href.
 * e.g. [hello_output.txt](sandbox:/mnt/data/hello_output.txt)
 */
const SANDBOX_LINK_PATTERN = /\[([^\]]+)\]\((sandbox:[^)]+)\)/g;

/**
 * Pattern to match raw HTML anchors with sandbox: protocol.
 * Captures the raw sandbox href and inner text.
 * e.g. <a href="sandbox:/mnt/data/hello_output.txt">hello_output.txt</a>
 */
const SANDBOX_HTML_LINK_PATTERN = /<a\s+[^>]*href=(['"])(sandbox:[^'"]+)\1[^>]*>(.*?)<\/a>/gi;

/**
 * Extracts the filename from either a raw sandbox href or the internal hash-based href.
 */
export function getSandboxFilename(href: string): string {
  if (href.startsWith('#sandbox-file:')) {
    return decodeURIComponent(href.replace('#sandbox-file:', ''));
  }

  if (href.startsWith('sandbox:')) {
    const sanitizedHref = href.replace(/^sandbox:/, '');
    const segments = sanitizedHref.split('/').filter(Boolean);
    return decodeURIComponent(segments.at(-1) ?? '');
  }

  return '';
}

/**
 * Pre-processes markdown to replace sandbox: file links with hash-based URLs
 * that can be intercepted by the `a` component override in markdown rendering.
 *
 * Converts: [hello_output.txt](sandbox:/mnt/data/hello_output.txt)
 * To:       [hello_output.txt](#sandbox-file:hello_output.txt)
 *
 * Uses the basename from the URL path (not the link text) for reliable filename matching.
 */
export function preprocessSandboxLinks(markdown: string): string {
  const replaceWithSandboxFileHref = (linkText: string, href: string) => {
    const filename = getSandboxFilename(href);
    return `[${linkText}](#sandbox-file:${encodeURIComponent(filename)})`;
  };

  return markdown
    .replace(SANDBOX_LINK_PATTERN, (_match, linkText, href) => replaceWithSandboxFileHref(linkText, href))
    .replace(SANDBOX_HTML_LINK_PATTERN, (_match, _quote, href, linkText) =>
      replaceWithSandboxFileHref(String(linkText), String(href)),
    );
}

/**
 * Checks whether markdown contains any sandbox file links, either markdown or raw HTML.
 */
export function containsSandboxLinks(markdown: string): boolean {
  const markdownPattern = new RegExp(SANDBOX_LINK_PATTERN.source, SANDBOX_LINK_PATTERN.flags);
  const htmlPattern = new RegExp(SANDBOX_HTML_LINK_PATTERN.source, SANDBOX_HTML_LINK_PATTERN.flags);
  return markdownPattern.test(markdown) || htmlPattern.test(markdown);
}

/**
 * Checks if an href is a sandbox file link, either raw or preprocessed.
 */
export function isSandboxFileHref(href: string): boolean {
  return href.startsWith('#sandbox-file:') || href.startsWith('sandbox:');
}
